import { Tag, baseApi } from './base'
import { Policy } from './policy'

import { ExternalService } from '@/constants/external-services'

import { cacher } from '@/utils/rtkQueryCacheUtils'

export type License = {
  _id: string
  components: string[]
  name: string
  description: string
  isDefault: boolean
  policies: Policy[]
  services: Array<ExternalService | string>
}

export type CreateLicenseParams = {
  components: string[]
  description?: string
  isDefault?: boolean
  name: string
  policies: string[]
  services: Array<ExternalService | string>
}

export type UpdateLicenseParams = Partial<CreateLicenseParams>

type GetLicensesParams = {
  establishment?: string
  id?: string[]
}

export const licenseApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getLicense: builder.query<License, string>({
      query: (licenseId) => `/licenses/${licenseId}`,
    }),
    getLicenses: builder.query<License[], GetLicensesParams>({
      query: (query) => ({
        url: `/licenses`,
        method: 'GET',
        params: query,
      }),
      providesTags: cacher.providesList(Tag.LICENSE),
    }),
    createLicense: builder.mutation<License, CreateLicenseParams>({
      query: (body) => ({
        url: '/licenses',
        method: 'POST',
        body,
      }),
      invalidatesTags: cacher.invalidatesList(Tag.LICENSE),
    }),
    updateLicense: builder.mutation<License, UpdateLicenseParams & { id: string }>({
      query: ({ id, ...body }) => ({
        url: `/licenses/${id}`,
        method: 'PUT',
        body,
      }),
      async onQueryStarted({ id }, { dispatch, queryFulfilled }) {
        try {
          const { data: updatedLicense } = await queryFulfilled
          dispatch(
            licenseApi.util.updateQueryData('getLicense', id, (draftLicense) => {
              Object.assign(draftLicense, updatedLicense)
            })
          )
        } catch (error) {
          console.log(error)
        }
      },
    }),
    deleteLicense: builder.mutation<License, string>({
      query: (licenseId) => ({
        url: `/licenses/${licenseId}`,
        method: 'DELETE',
      }),
      invalidatesTags: cacher.invalidatesList(Tag.LICENSE),
    }),
    deleteLicenses: builder.mutation<void, string[]>({
      query: (licenseIds) => ({
        url: '/licenses/delete',
        method: 'POST',
        body: { id: licenseIds },
      }),
      invalidatesTags: cacher.invalidatesList(Tag.LICENSE),
    }),
  }),
  overrideExisting: false,
})
